<template>
  <div class="record-field-sections panel-sections edge-to-edge">
    <!-- Overview: explicit paired layout -->
    <FieldGrid
      v-if="overviewLayout.length > 0"
      :fields="overviewLayout"
      :dv="dv"
    />
    <hr v-if="overviewLayout.length > 0 && detailLayout.length > 0" />

    <!-- Detail fields: everything not placed in a named section -->
    <FieldGrid
      v-if="detailLayout.length > 0"
      :fields="detailLayout"
      :dv="dv"
      :cell-slots="$slots"
    />

    <ToggleSection
      v-if="hasField('required_annotations')"
      icon-classes="fa-solid fa-tags"
      :collapsed="!dv.isEditing"
    >
      <template #label>
        Experimental Factors & Required Annotations
        <span class="tag tag-count is-rounded is-primary">
          {{ requiredAnnotationsCount }}
        </span>
      </template>
      <FieldGrid :fields="[['required_annotations']]" :dv="dv" />
    </ToggleSection>

    <ToggleSection
      v-if="hasField('contributors')"
      icon-classes="fa-solid fa-users"
      :collapsed="!dv.isEditing"
    >
      <template #label>
        Contributors
        <span class="tag tag-count is-rounded is-primary">
          {{ contributorsCount }}
        </span>
      </template>
      <FieldGrid :fields="[['contributors']]" :dv="dv" />
    </ToggleSection>

    <ToggleSection
      v-if="dv.ownershipLifecycleGrid.length > 0"
      label="Ownership and Lifecycle"
      icon-classes="fa-solid fa-arrows-spin"
      :collapsed="!dv.isEditing"
    >
      <FieldGrid :fields="dv.ownershipLifecycleGrid" :dv="dv" />
    </ToggleSection>

    <ToggleSection
      v-if="hasField('permissions')"
      label="Sharing"
      icon-classes="fa-solid fa-share-nodes"
      :collapsed="!dv.isEditing"
    >
      <FieldGrid :fields="[['permissions']]" :dv="dv" />
    </ToggleSection>
  </div>
</template>

<script>
import { computed } from 'vue'

import { OWNERSHIP_LIFECYCLE_FIELD_NAMES } from '@/constants'

import FieldGrid from '@/components/fields/FieldGrid.vue'
import ToggleSection from '@/components/fields/ToggleSection.vue'

// Explicit grid layouts — FieldGrid-compatible nested arrays
const OVERVIEW_FIELDS = [
  ['id', 'code'],
  ['name', 'responsible_person'],
  ['stock_locations_breadcrumb'],
  ['remote_repository'],
]

const SECTION_FIELDS = [
  ['permissions'],
  ['contributors'],
  ['required_annotations'],
]

// Fields always excluded from detailLayout: dedicated sections + ownership lifecycle.
// OVERVIEW_FIELDS are NOT statically excluded here — only the ones that actually
// render in overviewLayout are excluded dynamically, so fields absent from
// visibleFields fall through to detailLayout instead of disappearing entirely.
const ALWAYS_EXCLUDED = new Set(
  [...OWNERSHIP_LIFECYCLE_FIELD_NAMES, ...SECTION_FIELDS.flat()].filter(
    Boolean,
  ),
)

export default {
  name: 'RecordFieldSections',
  components: { FieldGrid, ToggleSection },
  props: {
    dv: { type: Object, required: true },
    excludeFields: { type: Array, default: () => [] },
  },
  setup(props) {
    const visibleSet = computed(
      () => new Set((props.dv.visibleFields || []).map((f) => f.name)),
    )
    const hasField = (name) => visibleSet.value.has(name)

    const baseExcluded = computed(() => {
      const set = new Set(ALWAYS_EXCLUDED)
      for (const name of props.excludeFields) set.add(name)
      return set
    })

    // Overview: filter rows to only include fields that exist for this model type
    // and are not excluded by the caller (e.g. already placed elsewhere on the page).
    const overviewLayout = computed(() =>
      OVERVIEW_FIELDS.map((row) =>
        row.filter((name) => hasField(name) && !baseExcluded.value.has(name)),
      ).filter((row) => row.length > 0),
    )

    // Detail: listed fields minus fields that actually landed in overviewLayout
    // (dynamic) plus always-excluded sections/ownership and caller exclusions.
    const detailLayout = computed(() => {
      const overviewPlaced = new Set(overviewLayout.value.flat())
      return (props.dv.listedFields || props.dv.visibleFields || [])
        .filter(
          (f) => !overviewPlaced.has(f.name) && !baseExcluded.value.has(f.name),
        )
        .map((f) => [f.name])
    })

    const requiredAnnotationsCount = computed(
      () => props.dv.record?.required_annotations?.value?.length ?? 0,
    )
    const contributorsCount = computed(
      () => props.dv.record?.contributors?.value?.length ?? 0,
    )

    return {
      overviewLayout,
      detailLayout,
      hasField,
      requiredAnnotationsCount,
      contributorsCount,
    }
  },
}
</script>

<style scoped>
hr {
  color: var(--border-default);
  opacity: 0.6;
  margin: 0 var(--panel-padding-x);
}
</style>
